// @flow

import * as React from 'react';

import { featureIsEnabled } from '@ta/common.features';
import { ShareCta } from '@ta/public.share-cta';
import { Linkify } from '@ta/social.links';
import { getDisplayName } from '@ta/social.member';
import { MemberEventOnObjectBlock } from '@ta/social.member-event';
import type { AuthZInfo } from '@ta/common.authz';
import {
  DisabledSocialAction,
  LikeAction,
  NestedSocialAction,
  BoostAction,
  SaveAction,
  SocialActionBar,
} from '@ta/social.actions';
import { isRepostSaveable } from '@ta/trips.trip-util';
import type { SocialObjectReference } from '@ta/social.types';
import SocialStatisticsBar from '@ta/social.statistics-bar';

import { CardSection } from '@ta/social.section-core';
import LinkPostSection from '@ta/social.section-link-post';
import ForumPostSection from '@ta/social.section-forum-post';
import ReviewSection from '@ta/social.section-review';
import PhotoSection from '@ta/social.section-photo';
import VideoSection from '@ta/social.section-video';
import MediaBatchPhotoSection from '@ta/social.section-media-batch-photo';
import TripSection from './TripSection';

import styles from './RepostSection.less';

type Props = {
  member: any,
  actor: any,
  repost: any,
  deleteObject: ?() => void,
  untagObject: ?() => void,
  loggedInUserId: string,
  isSingleObject: boolean,
  isEmbedded: boolean,
  loggedInMemberAuthorization?: AuthZInfo,
  clusterId?: string,
};

const RepostSection = ({
  repost,
  isSingleObject,
  isEmbedded,
  member,
  actor,
  deleteObject,
  loggedInMemberAuthorization,
  untagObject,
  loggedInUserId,
  clusterId,
}: Props) => {
  const [currentlyDisplayedImageIndex, setCurrentlyDisplayedImageIndex] = React.useState(0);
  if (!repost.object || !repost.object.repostedObject) {
    // Takes care of the case when a reposted piece of UGC was deleted
    return null;
  }

  const {
    __typename: repostedContentType,
    [repostedContentType === 'LinkPost' ? 'postId' : 'id']: repostedContentId,
  } = repost.object.repostedObject;
  const repostedObjectMember = repost.object.repostedObject.userProfile;
  const feedSectionItem = { object: repost.object.repostedObject };

  let cardSection = null;
  switch (repostedContentType) {
    case 'LinkPost':
      cardSection = (
        <LinkPostSection
          post={feedSectionItem}
          member={member}
          isEmbedded
          actor={repostedObjectMember}
        />
      );
      break;
    case 'ForumPost':
      cardSection = (
        <ForumPostSection
          post={feedSectionItem}
          member={member}
          isEmbedded
          actor={repostedObjectMember}
        />
      );
      break;
    case 'Video':
      cardSection = (
        <VideoSection
          video={feedSectionItem}
          member={member}
          isEmbedded
          actor={repostedObjectMember}
          actorForUrl={actor}
        />
      );
      break;
    case 'Review':
      cardSection = (
        <ReviewSection
          review={feedSectionItem}
          member={member}
          isEmbedded
          actor={repostedObjectMember}
          actorForUrl={actor}
          layout="REPOST_WITH_MODAL"
        />
      );
      break;
    case 'Photo':
      cardSection = (
        <PhotoSection
          photo={feedSectionItem}
          member={member}
          actor={repostedObjectMember}
          actorForUrl={actor}
          layout="REPOST_WITH_MODAL"
        />
      );
      break;
    case 'Trip':
      if (featureIsEnabled('trips_2018_home_profile')) {
        cardSection = (
          <TripSection
            trip={feedSectionItem}
            member={member}
            isEmbedded
            actor={repostedObjectMember}
          />
        );
      }
      break;
    case 'MediaBatch': {
      const media = feedSectionItem?.object?.media || [];
      const nonnullMedia = media.filter(Boolean);
      if (nonnullMedia.length) {
        cardSection = (
          <MediaBatchPhotoSection
            metadata={{
              __typename: feedSectionItem?.object?.__typename,
              orderedLocations: feedSectionItem?.object?.orderedLocations,
              mediaBatchId: feedSectionItem?.object?.mediaBatchId,
              route: feedSectionItem?.object?.route,
              socialReferences: feedSectionItem?.object?.socialReferences,
              socialStatistics: feedSectionItem?.object?.socialStatistics,
              created: feedSectionItem?.object?.created,
              caption: feedSectionItem?.object?.caption,
            }}
            photos={nonnullMedia}
            layout="REPOST_WITH_MODAL"
            member={member}
            actor={repostedObjectMember}
            actorForUrl={actor}
            currentlyDisplayedImageIndex={currentlyDisplayedImageIndex}
            setCurrentlyDisplayedImageIndex={setCurrentlyDisplayedImageIndex}
          />
        );
      }
      break;
    }
    default:
      cardSection = null;
  }

  const header = (
    <React.Fragment>
      <MemberEventOnObjectBlock
        objectEventType="onRepost"
        object={repost.object}
        member={member}
        actor={actor}
        deleteObject={deleteObject}
        untagObject={untagObject}
        loggedInUserId={loggedInUserId}
        isEmbedded={isEmbedded}
        overflowMenuObjectForType={
          repost.object?.repostedObject?.__typename === 'MediaBatch'
            ? {
                REPORT: (repost.object?.repostedObject?.media?.[currentlyDisplayedImageIndex]: any),
              }
            : undefined
        }
      />

      {repost.object.comment && repost.object.comment.length && (
        <div className={styles.comment}>
          <Linkify text={repost.object.comment} socialReferences={repost.object.socialReferences} />
        </div>
      )}
    </React.Fragment>
  );

  const content = [
    <CardSection.Content fullWidth key={repost.object.repostId}>
      {cardSection}
    </CardSection.Content>,
  ];

  const repostRef: SocialObjectReference = {
    id: String(repost.object.repostId),
    type: repost.object.__typename,
  };

  const boostAction =
    featureIsEnabled('social_boosting_enabled') &&
    (loggedInMemberAuthorization && loggedInMemberAuthorization.canBoostContent) ? (
      <BoostAction
        socialObjectRef={{ type: 'REPOST', id: repostRef.id }}
        socialStatistics={repost.object.socialStatistics}
        typename={repost.object.__typename}
      />
    ) : null;

  const repostProps = {
    modalName: `repost_${repostRef.type}_${repostRef.id}_${repostedContentId}`,
    repostPreviewProps: { feedSectionItem, member, actor: repostedObjectMember },
    originalRepostReference: repostRef,
    actor,
    socialStatistics: repost.object.socialStatistics,
  };

  const socialStatisticsBar = (
    <SocialStatisticsBar
      stats={repost.object.socialStatistics}
      socialRef={{
        type: repostRef.type,
        id: `${repostRef.id}`,
      }}
      socialProofParams={{ displayName: getDisplayName(actor) }}
      deduplicate
    />
  );

  const socialActionBar = (
    <SocialActionBar>
      <NestedSocialAction padRight>
        {/* TRVX-13661: Likes -> Helpful Tech Debt */}
        <LikeAction
          item={repost.object}
          socialObject={{ objectType: 'REPOST', objectId: repostRef.id }}
        />
        {featureIsEnabled('trips_save_ugc_reposts') && isRepostSaveable(repost) && (
          <SaveAction object={repost.object} displayStyle="icon+text" />
        )}
      </NestedSocialAction>
      <NestedSocialAction>
        {/* Share: TRVX-9358 */}
        {featureIsEnabled('social_ugc_share') && repost.object.route && repost.object.route.url ? (
          <ShareCta
            url={`${repost.object.route.url}`}
            itemType="Repost"
            itemId={repost.object.repostId}
            styleAsSocialAction
            alignEdge="right"
            repostProps={repostProps}
          />
        ) : (
          <DisabledSocialAction actionType="Share" />
        )}
        {boostAction}
      </NestedSocialAction>
    </SocialActionBar>
  );

  return cardSection ? (
    <CardSection
      isSingleObject={isSingleObject}
      isEmbedded={isEmbedded}
      header={header}
      content={content}
      socialStatisticsBar={socialStatisticsBar}
      socialActionBar={socialActionBar}
      pois={[]}
      fullWidth
      clusterId={clusterId}
    />
  ) : null;
};

RepostSection.defaultProps = {
  isSingleObject: false,
  isEmbedded: false,
  member: null,
  deleteObject: null,
  loggedInMemberAuthorization: undefined,
  untagObject: null,
  loggedInUserId: '',
  clusterId: undefined,
};

export default RepostSection;
